<template>
  <div v-if="show">
    <slot></slot>
  </div>
</template>

<script>
export default {
  name: "JcDialog",
  props: {
    loading: Boolean,
    show: Boolean,
    /**
     * Title of the dialog.
     * Also accepts a function that returns a string.
     */
    title: String | Function,
    /**
     * Class that will be applied to the title.
     */
    titleClass: String,
    type: {
      type: String,
      default: 'default'
    },
    typeAnimated: {
      type: Boolean,
      default: true
    },
    draggable: {
      type: Boolean,
      default: true
    },
    dragWindowGap: {
      type: Number,
      default: 15
    },
    dragWindowBorder: {
      type: Boolean,
      default: true
    },
    animateFromElement: {
      type: Boolean,
      default: true
    },
    alignMiddle: {
      type: Boolean,
      default: true
    },
    smoothContent: {
      type: Boolean,
      default: true
    },
    content: {
      type: String | Function,
    },
    contentLoaded: Function,
    buttons: Object,
    icon: String,
    lazyOpen: {
      type: Boolean,
      default: true
    },
    bgOpacity: Float32Array,
    theme: {
      type: String,
      default: 'light'
    },
    animation: {
      type: String,
      default: 'zoom'
    },
    closeAnimation: {
      type: String,
      default: 'scale'
    },
    animationSpeed: {
      type: Number,
      default: 400
    },
    animationBounce: {
      type: Number,
      default: 1
    },
    escapeKey: {
      type: Boolean,
      default: true
    },
    rtl: {
      type: Boolean,
      default: false
    },
    container: {
      type: String,
      default: 'body'
    },
    containerFluid: {
      type: Boolean | String | Function,
      default: false
    },
    backgroundDismiss: {
      type: Boolean | String | Function,
      default: false
    },
    backgroundDismissAnimation: {
      type: String,
      default: 'shake'
    },
    autoClose: {
      type: Boolean,
      default: false
    },
    closeIcon: {
      type: Boolean,
      default: true
    },
    closeIconClass: {
      type: String,
      default: ''
    },
    watchInterval: {
      type: Number,
      default: 100
    },
    columnClass: {
      type: String,
      default: 'col-md-4 col-md-offset-4 col-sm-6 col-sm-offset-3 col-xs-10 col-xs-offset-1'
    },
    useBootstrap: {
      type: Boolean,
      default: true
    },
    boxWidth: {
      type: String,
      default: "50%"
    },
    scrollToPreviousElement: {
      type: Boolean,
      default: true
    },
    scrollToPreviousElementAnimate: {
      type: Boolean,
      default: true
    },
    offsetTop: {
      type: Number,
      default: 40
    },
    offsetBottom: {
      type: Number,
      default: 40
    },
    bootstrapClasses: {
      type: Object,
      default: () => {
        return {container: 'container', containerFluid: 'container-fluid', row: 'row',};
      }
    },
    confirmAction: Function,
    btnConfirmText: String,
  },
  data() {
    return {
      jc: {},
      redConfig: {
        type: 'red',
        icon: 'fas fa-exclamation-triangle',
        title: 'Cảnh báo!',
        escapeKey: true,
        backgroundDismiss: true,
        buttons: {
          confirm: {
            btnClass: "btn-danger btn-sm"
          }
        }
      }
    }
  },
  methods: {
    setTitle(title) {
      this.jc.setTitle(title);
    },
    setIcon(iconClass) {
      this.jc.setIcon(iconClass);
    },
    setContent(content) {
      this.jc.setContent(content);
    },
    setContentPrepend(content) {
      this.jc.setContentPrepend(content);
    },
    setContentAppend(content) {
      this.jc.setContentAppend(content);
    },
    setType(typeClass) {
      this.jc.setType(typeClass);
    },
    showLoading(disableButtons) {
      this.jc.showLoading(disableButtons);
    },
    hideLoading(enableButtons) {
      this.jc.hideLoading(enableButtons);
    },
    close() {
      return this.jc.close();
    },
    open() {
      let vue = this;
      vue.jc = $.confirm({...vue.config, content: vue.$el});
      return vue.jc.open();
    },
    toggle() {
      this.jc.toggle();
    },
    highlight() {
      this.jc.highlight();
    },
    setBoxWidth(width) {
      this.jc.setBoxWidth(width);
    },
    setColumnClass(className) {
      this.jc.setColumnClass(className);
    },
    setTheme(themeName) {
      this.jc.setTheme(themeName);
    },
    setTextButton(buttonName, text) {
      this.jc.buttons[buttonName].setText(text);
    },
    addClassButton(buttonName, className) {
      this.jc.buttons[buttonName].addClass(className);
    },
    removeClassButton(buttonName, className) {
      this.jc.buttons[buttonName].removeClass(className);
    },
    disableButton(buttonName) {
      this.jc.buttons[buttonName].disable();
    },
    enableButton(buttonName) {
      this.jc.buttons[buttonName].enable();
    },
    hideButton(buttonName) {
      this.jc.buttons[buttonName].hide();
    },
    showButton(buttonName) {
      this.jc.buttons[buttonName].show();
    },
    getBodyDOM() {
      return this.jc.$body;
    },
    getContentDOM() {
      return this.jc.$content;
    },
    getTitleDOM() {
      return this.jc.$title;
    },
    getIconDOM() {
      return this.jc.$icon;
    },
    getTargetDOM() {
      return this.jc.$target;
    },
  },
  watch: {
    title: function (newValue, oldValue) {
      if (this.jc) {
        this.setTitle(newValue);
      }
    },
    icon: function (newValue, oldValue) {
      if (this.jc) {
        this.setIcon(newValue);
      }
    },
    type: function (newValue, oldValue) {
      if (this.jc) {
        this.setType(newValue);
      }
    },
    show: function (newValue) {
      if (newValue) {
        this.open();
      } else {
        this.close();
      }
    },
  },
  computed: {
    config: function () {
      const vue = this;
      let btnConfirmClass = this.type == 'red' ? 'danger' :
          this.type == 'orange' ? 'warning' :
              'success';
      let defaultButton = {
        confirm: {
          text: vue.btnConfirmText || 'Xác nhận',
          btnClass: `btn-${btnConfirmClass} btn-sm`,
          action: function () {
            vue.$emit("onConfirm");
            return false;
          }
        },
        close: {
          text: 'Đóng',
          btnClass: 'btn-secondary btn-sm btn-simple',
          action: function () {
            vue.$emit('onClose')
          }
        },
      }

      let config = {
        onContentReady: () => {
          this.$emit("onContentReady");
        },
        onOpenBefore: () => {
          this.$emit("onOpenBefore");
        },
        onOpen: () => {
          this.$emit("update:show", true);
          this.$emit("onOpen");
        },
        onClose: () => {
          this.$emit("update:show", false);
          this.$emit("onClose");
        },
        onDestroy: () => {
          this.$emit("onDestroy");
        },
        onAction: () => {
          this.$emit("onAction");
        }
      }

      config.title = vue.title;
      config.titleClass = vue.titleClass;
      config.type = vue.type;
      config.typeAnimated = vue.typeAnimated;
      config.draggable = vue.draggable;
      config.dragWindowGap = vue.dragWindowGap;
      config.dragWindowBorder = vue.dragWindowBorder;
      config.animateFromElement = vue.animateFromElement;
      config.alignMiddle = vue.alignMiddle;
      config.smoothContent = vue.smoothContent;
      config.content = vue.content || vue.$el;
      config.contentLoaded = vue.contentLoaded;
      config.buttons = vue.buttons || defaultButton;
      config.icon = vue.icon;
      config.lazyOpen = vue.lazyOpen;
      config.bgOpacity = vue.bgOpacity;
      config.theme = vue.theme;
      config.animation = vue.animation;
      config.closeAnimation = vue.closeAnimation;
      config.animationSpeed = vue.animationSpeed;
      config.animationBounce = vue.animationBounce;
      config.escapeKey = vue.escapeKey;
      config.rtl = vue.rtl;
      config.container = vue.container;
      config.containerFluid = vue.containerFluid;
      config.backgroundDismiss = vue.backgroundDismiss;
      config.backgroundDismissAnimation = vue.backgroundDismissAnimation;
      config.autoClose = vue.autoClose;
      config.closeIcon = vue.closeIcon;
      config.closeIconClass = vue.closeIconClass;
      config.watchInterval = vue.watchInterval;
      config.columnClass = vue.columnClass;
      config.useBootstrap = vue.useBootstrap;
      config.boxWidth = vue.boxWidth;
      config.scrollToPreviousElement = vue.scrollToPreviousElement;
      config.scrollToPreviousElementAnimate = vue.scrollToPreviousElementAnimate;
      config.offsetTop = vue.offsetTop;
      config.offsetBottom = vue.offsetBottom;
      config.bootstrapClasses = vue.bootstrapClasses;

      return config;
    },
    isClosed() {
      return this.jc && this.jc.isClosed && this.jc.isClosed();
    },
    isOpen() {
      return this.jc && this.jc.isOpen && this.jc.isOpen();
    },
  },
  mounted() {
    const vue = this;
    vue.jc = $.confirm({...vue.config, content: vue.$el});
  },
  destroyed() {
    console.log('jc dialog destroyed')
  }
}
</script>

<style scoped>

</style>